import {useCallback, useEffect, useState} from 'react';

import {API_CONFIG} from '../contentGenerator/config';
import {apiFetch} from './apiFetch';

const REVIEW_CONFIGS_URL = `${API_CONFIG.PROXY_URL}/review-configs`;

const normalizeCounts = (counts = {}) => ({
    groups: Number(counts.groups) || 0,
    listings: Number(counts.listings) || 0,
    fields: Number(counts.fields) || 0,
});

const normalizeStringList = (value) =>
    Array.isArray(value)
        ? value.filter((item) => typeof item === 'string' && item.trim())
        : [];

const normalizeSubgroupEntry = (entry = {}, parentType = 'bullet') => ({
    uniqueVarNames: Array.isArray(entry.uniqueVarNames)
        ? entry.uniqueVarNames.filter(Boolean)
        : [],
    valuesByName:
        entry.valuesByName && typeof entry.valuesByName === 'object'
            ? {...entry.valuesByName}
            : {},
    text: typeof entry.text === 'string' ? entry.text : '',
    ...(parentType === 'faq'
        ? {question: typeof entry.question === 'string' ? entry.question : ''}
        : {}),
    status: typeof entry.status === 'string' ? entry.status : 'draft',
    searchTerms: normalizeStringList(entry.searchTerms),
    ignoredIssueIds: normalizeStringList(entry.ignoredIssueIds),
});

const normalizeField = (field = {}) => {
    const type = typeof field.type === 'string' ? field.type : 'bullet';
    const base = {
        id: typeof field.id === 'string' ? field.id : '',
        type,
        label: typeof field.label === 'string' ? field.label : '',
    };

    if (Array.isArray(field.bySubgroup)) {
        return {
            ...base,
            bySubgroup: field.bySubgroup.map((entry) => normalizeSubgroupEntry(entry, type)),
        };
    }

    return {
        ...base,
        text: typeof field.text === 'string' ? field.text : '',
        ...(type === 'faq'
            ? {question: typeof field.question === 'string' ? field.question : ''}
            : {}),
        status: typeof field.status === 'string' ? field.status : 'draft',
        searchTerms: normalizeStringList(field.searchTerms),
        ignoredIssueIds: normalizeStringList(field.ignoredIssueIds),
    };
};

const normalizeGroup = (group = {}) => ({
    groupKey: typeof group.groupKey === 'string' ? group.groupKey : '',
    skus: normalizeStringList(group.skus),
    fields: Array.isArray(group.fields) ? group.fields.map(normalizeField) : [],
});

export const normalizeReviewConfig = (config = {}) => ({
    id: typeof config.id === 'string' ? config.id : '',
    name: typeof config.name === 'string' ? config.name : 'Untitled config',
    version: Number(config.version) || 1,
    createdAt: typeof config.createdAt === 'string' ? config.createdAt : '',
    updatedAt: typeof config.updatedAt === 'string' ? config.updatedAt : '',
    counts: normalizeCounts(config.counts),
    groups: Array.isArray(config.groups) ? config.groups.map(normalizeGroup) : [],
});

const normalizeConfigList = (payload = {}) =>
    (Array.isArray(payload?.configs) ? payload.configs : [])
        .map(normalizeReviewConfig)
        .filter((config) => config.id && config.groups.length);

async function parseApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) {
        throw new Error(
            payload?.error ||
                payload?.message ||
                (typeof payload === 'string' && payload) ||
                fallbackMessage,
        );
    }

    return payload;
}

export async function loadReviewConfigs() {
    if (typeof fetch !== 'function') return [];

    try {
        const response = await apiFetch(REVIEW_CONFIGS_URL);
        const payload = await parseApiResponse(
            response,
            'Could not load saved review configurations.',
        );
        return normalizeConfigList(payload);
    } catch {
        return [];
    }
}

export async function createReviewConfig(config) {
    const response = await apiFetch(REVIEW_CONFIGS_URL, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(config),
    });
    const payload = await parseApiResponse(
        response,
        'Could not save the review configuration.',
    );
    return normalizeReviewConfig(payload);
}

export async function updateReviewConfig(id, config) {
    const response = await apiFetch(`${REVIEW_CONFIGS_URL}/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(config),
    });
    const payload = await parseApiResponse(
        response,
        'Could not update the review configuration.',
    );
    return normalizeReviewConfig(payload);
}

export async function deleteReviewConfig(id) {
    const response = await apiFetch(`${REVIEW_CONFIGS_URL}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
    await parseApiResponse(response, 'Could not delete the review configuration.');
    return true;
}

export function useReviewConfigs() {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const loadedConfigs = await loadReviewConfigs();
            setConfigs(loadedConfigs);
            setError('');
            return loadedConfigs;
        } catch (err) {
            setError(err.message);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let active = true;

        (async () => {
            setLoading(true);
            const loadedConfigs = await loadReviewConfigs();
            if (!active) return;
            setConfigs(loadedConfigs);
            setLoading(false);
        })();

        return () => {
            active = false;
        };
    }, []);

    const saveConfig = useCallback(async ({id = '', config}) => {
        setSaving(true);
        try {
            const savedConfig = id
                ? await updateReviewConfig(id, config)
                : await createReviewConfig(config);
            setConfigs((currentConfigs) => {
                const filtered = currentConfigs.filter(
                    (candidate) =>
                        candidate.id !== savedConfig.id &&
                        candidate.name.toLowerCase() !== savedConfig.name.toLowerCase(),
                );
                return [savedConfig, ...filtered];
            });
            setError('');
            return savedConfig;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setSaving(false);
        }
    }, []);

    const removeConfig = useCallback(async (id) => {
        setSaving(true);
        try {
            await deleteReviewConfig(id);
            setConfigs((currentConfigs) =>
                currentConfigs.filter((config) => config.id !== id),
            );
            setError('');
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setSaving(false);
        }
    }, []);

    return {
        configs,
        error,
        loading,
        refresh,
        removeConfig,
        saveConfig,
        saving,
    };
}
